import React, { useState, useRef, useEffect } from 'react'
import {
  Box, Typography, TextField, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, IconButton, ListItemIcon
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useDraftPersona } from './useDraftPersona'
import { useAuth } from '../../hooks/useAuth'
// import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LinkIcon from '@mui/icons-material/Link'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { useNavigate } from 'react-router-dom'
import { Timestamp } from 'firebase/firestore'
import { signInAnonymously } from 'firebase/auth'
import { auth, firestorage, firestore } from '../../utils/firebase'
import { doc, setDoc } from 'firebase/firestore'
import { updatePersona } from '../../utils/api'
import { USER_TYPE } from '../../config/const'
import { toast } from 'react-toastify'
import Creatinganimation from '../../assets/animations/Creatinganimation.json';
import Lottie from "lottie-react"
import { ref, uploadBytes } from 'firebase/storage'
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';



const CreatePersonaForm: React.FC = () => {
  const { setLoginModalVisible } = useAuth()
  const { draft, addBasicInfo, addLink, removeLink, addFile, removeFile } = useDraftPersona()
  const formRef = useRef<HTMLDivElement>(null)

  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkInput, setLinkInput] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)
  const theme = useTheme();
  const [nameError, setNameError] = useState<string | null>(null)
  const [trainingMaterialError, setTrainingMaterialError] = useState<string | null>(null)

  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollTop = formRef.current.scrollHeight
    }
  }, [draft.links.length, draft.files.length])

  function handleFileBrowse() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '*/*'
    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) return
      addFile(file)
    }
    input.click()
  }

  const confirmAddLink = () => {
  const raw = linkInput.trim();
  if (!raw) {
    setLinkError('URL is required');
    return;
  }

  const candidate = raw.startsWith('http') ? raw : `https://${raw}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    setLinkError('Please enter a valid URL');
    return;
  }
  if (!parsed.hostname.includes('.')) {
    setLinkError('Please enter a valid web address');
    return;
  }

  addLink(parsed.toString());
  setLinkInput('');
  setLinkError(null);
  setLinkDialogOpen(false);
};

  const canCreate =
    draft.fullName.trim() !== '' &&
    (draft.files.length > 0 || draft.links.length > 0)

    const handleCreate = async (e: React.MouseEvent) => {
      e.preventDefault()
      
      // Validate name
      if (!draft.fullName.trim()) {
        setNameError('Name is required')
        return
      }
      setNameError(null)

      // Validate training material
      if (draft.files.length === 0 && draft.links.length === 0) {
        setTrainingMaterialError('Please add at least one document or link')
        return
      }
      setTrainingMaterialError(null)

      setIsCreating(true)

      try {
        const { user } = await signInAnonymously(auth)

        const base = draft.fullName
          .trim()
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
        const suffix = Math.random().toString(36).substring(2, 6)
        const userName = `${base}-${suffix}`

        const uploadUserDocuments = async (userDocument: any, userId: string) => {
          const documentsPath: Record<any, any>[] = [];
          //check user document array has data
          if (userDocument && userDocument?.length >= 1) {
            const timeStamp = Timestamp.fromDate(new Date());
            // get uploaded documents upload status from firebase
            const documentsUploadStatus = await Promise.all<boolean>(
              userDocument.map(async (documentItem: any) => {
                const documentOrgName = documentItem?.name;
                const documentPath = `files/${userId}/${new Date().getTime()}_${
                  documentItem?.name
                }`;
                const documentStorageRef = ref(firestorage, documentPath);
                try {
                  await uploadBytes(documentStorageRef, documentItem?.file);
                  documentsPath.push({
                    name: documentOrgName,
                    path: documentPath,
                    uploadedAt: timeStamp,
                  });
                  return true;
                } catch (err) {
                  console.error(err);
                  return false;
                }
              })
            );
            const rejectedIndex = documentsUploadStatus.findIndex(
              (item: boolean) => !item
            );
            if (rejectedIndex !== -1) {
              return false;
            }
          }
          return documentsPath;
        };

        let processedDocuments = draft.files.length > 0 ?
          await uploadUserDocuments(draft.files, user.uid) : [];

        const docRef = doc(firestore, 'users', user.uid)
        await setDoc(docRef, {
          fullName: draft.fullName,
          shortBio: draft.description,
          userType: USER_TYPE.CREATOR,
          userName,
          normalizedUserName: userName.toLowerCase(),
          userWebPage: draft.links.map(link => ({
            web_link: link,
            disabled: false
          })),
          userDocument: processedDocuments,
          createdAt: Timestamp.now(),
        }, { merge: true })

        const payload = {
          index_documents: draft.files.length > 0,
          index_webpage: draft.links.length > 0,
          index_twitter: false,
          index_yt: false,
          index_sitemap: false,
        }

        await updatePersona(user.uid, payload)

        navigate(`/${userName}/chat`)

      } catch (err: any) {
        console.error('[CreatePersonaForm] updatePersona failed:', err.response?.data ?? err)
        toast.error(
          'Indexing error: ' +
          (err.response?.data?.message || err.message || 'please check console')
        )
      } finally {
        setIsCreating(false)
      }
    }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    files.forEach(file => addFile(file))
  }

  const maxVisible = 2
  let linkCount = showAll ? draft.links.length : Math.min(draft.links.length, maxVisible)
  let remaining = showAll ? draft.files.length : maxVisible - linkCount
  let fileCount = showAll ? draft.files.length : Math.min(draft.files.length, remaining)
  const visibleLinks = draft.links.slice(0, linkCount)
  const visibleFiles = draft.files.slice(0, fileCount)
  const totalItems = draft.links.length + draft.files.length

  return (
    <>
      <Box
        ref={formRef}
        sx={{
          border: "1px solid #0810FF",
          borderRadius: 2,
          p: 3,
          bgcolor: (theme) => theme.palette.background['paper-elevation-4'],
          boxShadow: (theme) => theme.shadows[4],
          width: { xs: '100%', md: '100%' },
          minWidth: { xs: '100%', md: '100%' },
          maxWidth: { xs: '100%', md: '100%' },
          boxSizing: 'border-box',
          overflow: 'auto',
          height: { xs: 'auto', md: '100%' },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography variant="h4" align="center" sx={{ 
          color: theme.palette.mode === "dark" ? "#D1D2FF" : "#2725FC",
          mb: 0.5
        }}>
          Create Your Free Verified AI Persona
        </Typography>
        <Typography variant="body1" color="text.primary" align="center" sx={{ mb: 2 }}>
          Create an interactive digital copy of you or your brand to engage with others 24/7.
        </Typography>

        <Stack spacing={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <Typography variant="h7" color="text.primary" sx={{ mb: 1 }}>
              Name&nbsp;<Typography component="span" color="error">*</Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder="Jane Doe"
              value={draft.fullName}
              onChange={e => {
                addBasicInfo({ fullName: e.target.value })
                if (nameError) setNameError(null)
              }}
              InputProps={{ 
                sx: { 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'common.white',
                  color: 'text.primary'
                } 
              }}
              sx={{ mt: 1 }}
              error={!!nameError}
              helperText={nameError}
            />
          </Box>

          <Box>
            <Typography variant="h7" color="text.primary" sx={{ mb: 1 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              placeholder="Award-winning fitness coach…"
              value={draft.description}
              onChange={e => addBasicInfo({ description: e.target.value })}
              InputProps={{ 
                sx: { 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'common.white',
                  color: 'text.primary',
                  height: '100%'
                } 
              }}
              sx={{ 
                mt: 1,
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '100%'
                }
              }}
            />
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h7" color="text.primary">
              Add Training Material&nbsp;<Typography component="span" color="error">*</Typography>
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Add documents, links, and other content to train your AI Persona's personality.
            </Typography>
            {trainingMaterialError && (
              <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                {trainingMaterialError}
              </Typography>
            )}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: '#42424280',
                bgcolor: (theme) => theme.palette.background['paper-elevation-8'],
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 0
              }}
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Typography variant="h7" color="text.primary" mb={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CloudUploadIcon sx={{ mr: 1 }} />
                Drop files here
              </Typography>
              <Typography variant="body3" color="text.secondary" display="block" mb={1} >
                or
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="outlined" onClick={handleFileBrowse} startIcon={<UploadFileIcon />}>
                  Browse Files
                </Button>
                <Button variant="outlined" onClick={() => setLinkDialogOpen(true)} startIcon={<LinkIcon />}>
                  Add Link
                </Button>
              </Stack>
            </Box>
          </Box>

          {/* Show links */}
          {visibleLinks.length > 0 && (
            <List dense disablePadding sx={{ mb: 0 }}>
              {visibleLinks.map((link, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LinkIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={link} />
                  <IconButton size="small" onClick={() => removeLink(link)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* Show files */}
          {visibleFiles.length > 0 && (
            <List dense disablePadding sx={{ mt: 0 }}>
              {visibleFiles.map((doc, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <InsertDriveFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={doc.name} />
                  <IconButton size="small" onClick={() => removeFile(doc.name)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* See All button toggles full list */}
          {totalItems > maxVisible && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => setShowAll(!showAll)}>
              {showAll ? 'See less' : 'See all'}
            </Button>
          )}

          <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleCreate}
              startIcon={<AutoAwesomeIcon />}
              style={{ backgroundColor: '#0810FF', color: '#fff' }}
            >
              Create Your AI Persona
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
        <DialogTitle>Add a Training Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="standard"
            value={linkInput}
            onChange={e => {
              setLinkInput(e.target.value)
              if (linkError) setLinkError(null)
                }}
              error={!!linkError}
              helperText={linkError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmAddLink}>Add</Button>
        </DialogActions>
      </Dialog>
      <Dialog
      open={isCreating} 
      fullScreen
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none'
        }
      }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.5)' } }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            width: 360,
            p: 3,
            borderRadius: 2,
            textAlign: 'center',
            boxShadow: 3,
            bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
            color: 'text.primary'
          }}
        >
          <Typography variant="h5" sx={{ color: 'text.primary' }} gutterBottom>
            Creating your AI Persona
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }} gutterBottom>
            Your training material is processing...
          </Typography>
          <Box sx={{ mx: 'auto' }}>
            <Lottie
              animationData={Creatinganimation}
              loop
              style={{ width: 310, height: 300 }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>

    </>
  )
}

export default CreatePersonaForm
